import { useCallback, useEffect, useMemo, useState } from 'react'
import { useToast } from '@bonitour/components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from 'react-query'
import { ReportService } from 'core/services/Reports'
import { ExportVoucherReportSchema } from '../schemas/ExportVoucherReportSchema'
import { useUser } from 'contexts/User'
import { useTimeExpiration } from '@bonitour/app-functions'
import { identity } from '@bonitour/common-functions'

const EXPORT_OPTIONS = [
  { label: 'CSV', value: 'csv' }
]

const exportKey = 'voucherColumnsToExport'

export const useExportVoucher = (vouchersFilters = {}, handleCloseExportModal = identity) => {
  const { add: addToast } = useToast()
  const { user } = useUser()

  const [newEmail, setNewEmail] = useState('')
  const [pendingExportAll, setPendingExportAll] = useState(false)
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [initialFormValues] = useState({
    wantedFields: [],
    emails: [user.email],
    format: 'csv'
  })

  const {
    data: columnsToExport = []
  } = useQuery({
    queryFn: () => {
      return ReportService.listVouchersAttributesToExport({
        lang: 'pt-br'
      })
    },
    queryKey: [exportKey]
  })

  const {
    addTimer,
    isExpired,
    isStarted
  } = useTimeExpiration(30, exportKey)

  const {
    formState: { errors, isSubmitted },
    setValue,
    handleSubmit,
    watch,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(ExportVoucherReportSchema),
    defaultValues: initialFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const wantedFields = watch('wantedFields')
  const format = watch('format')
  const emails = watch('emails')

  const getParsedErrors = () => {
    return Object.keys(errors).reduce((acc, key) => {
      const error = errors[key]
      if (error) {
        if (typeof error === 'string') {
          acc[key] = error
        } else if (Array.isArray(error)) {
          acc[key] = error
        } else if (error?.message) {
          acc[key] = error.message
        }
      }
      return acc
    }, {})
  }

  const parsedErrors = getParsedErrors()

  const onSuccess = useCallback((formData) => {
    if (isLoadingExport || (!isExpired && isStarted)) return
    setIsLoadingExport(true)
    return ReportService.exportVouchers({
      emailAddresses: formData.emails,
      format: formData.format,
      wantedFields: formData.wantedFields,
      reportFilters: vouchersFilters || {}
    }).then(() => {
      addTimer()
      addToast('Relatório será enviado para seu e-mail e demais destinatários', 'success')
      reset()
      handleCloseExportModal()
    }).catch(() => {
      addToast('Houve um erro inesperado ao exportar')
    }).finally(() => {
      setIsLoadingExport(false)
    })
  }, [addTimer, addToast, handleCloseExportModal, isExpired, isLoadingExport, isStarted, reset, vouchersFilters])

  const onFailure = useCallback(() => {
    addToast('Erro ao exportar transações')
  }, [addToast])

  const onInputChange = useCallback((field) => async (value) => {
    setValue(field, value)
    if (isSubmitted) {
      trigger(field)
    }
  }, [isSubmitted, setValue, trigger])

  const handleEmails = useCallback(data => {
    if (typeof data === 'function') {
      onInputChange('emails')(data(emails || []))
    } else {
      onInputChange('emails')(data)
    }
  },
  [emails, onInputChange])

  const onFormSubmit = handleSubmit(onSuccess, onFailure)

  const isChecked = useCallback((column) => {
    return (wantedFields || []).includes(columnsToExport.find((item) => item.label === column)?.value)
  }, [columnsToExport, wantedFields])

  const onOptionChange = useCallback((column) => () => {
    const value = columnsToExport.find((item) => item.label === column)?.value
    if (isChecked(column)) {
      return setValue('wantedFields', wantedFields.filter((item) => item !== value))
    }
    setValue('wantedFields', [...wantedFields, value])
  }, [columnsToExport, isChecked, setValue, wantedFields])

  const isSelectedAll = useMemo(() => {
    return Object.keys(columnsToExport).length === wantedFields?.length
  }, [columnsToExport, wantedFields?.length])

  const onSelectAll = useCallback(() => {
    if (isSelectedAll) {
      return setValue('wantedFields', [])
    }
    const newWantedFields = columnsToExport.map((item) => item.value)
    setValue('wantedFields', newWantedFields)
  }, [columnsToExport, isSelectedAll, setValue])

  const onExport = useCallback((isExportAll = false) => () => {
    if (isExportAll && !isSelectedAll) {
      onSelectAll()
      setPendingExportAll(true)
      return
    }

    onFormSubmit()
  }, [isSelectedAll, onFormSubmit, onSelectAll])

  useEffect(() => {
    if (pendingExportAll && isSelectedAll) {
      setPendingExportAll(false)
      onFormSubmit()
    }
  }, [pendingExportAll, isSelectedAll, onFormSubmit])

  useEffect(() => {
    setValue('wantedFields', columnsToExport.map((item) => item.value))
  }, [columnsToExport, setValue])

  return {
    format,
    errors: parsedErrors,
    onOptionChange,
    isChecked,
    onSelectAll,
    isSelectedAll,
    onExport,
    setValue,
    onInputChange,
    reset,
    columnsToExport,
    newEmail,
    setNewEmail,
    exportOptions: EXPORT_OPTIONS,
    emails,
    handleEmails,
    isLoadingExport,
    isExpired,
    isStarted
  }
}
